import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, Globe, Users, Clock, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageTransition from "@/components/PageTransition";
import { teamMembers, getTeamMemberImageUrl, handleImageError, uploadTeamImages, prefetchTeamImages, staticTeamImageUrls } from "@/utils/teamImages";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

const About = () => {
  const [teamImages, setTeamImages] = useState<Record<string, string>>(staticTeamImageUrls);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  useEffect(() => {
    const loadTeamImages = async () => {
      try {
        setIsLoadingImages(true);
        
        const prefetchedImages = prefetchTeamImages();
        
        if (Object.keys(prefetchedImages).length === teamMembers.length) {
          setTeamImages(prefetchedImages);
        } else {
          await uploadTeamImages();
          
          const imageUrls: Record<string, string> = {};
          teamMembers.forEach(member => {
            imageUrls[member.image] = getTeamMemberImageUrl(member.image);
          });
          
          setTeamImages(imageUrls);
        }
      } catch (error) {
        console.error("Failed to load team images:", error);
        toast.error("Failed to load team images. Using fallbacks instead.");
        
        const fallbackUrls: Record<string, string> = {};
        teamMembers.forEach(member => {
          fallbackUrls[member.image] = "https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=320&h=320&q=80";
        });
        setTeamImages(fallbackUrls);
      } finally {
        setIsLoadingImages(false);
      }
    };
    
    loadTeamImages();
  }, []);

  return (
    <PageTransition>
      <Helmet>
        <title>About DesertSEO | LA SEO Company | Team & Mission</title>
        <meta name="description" content="Meet the DesertSEO team — a leading Los Angeles SEO company. Learn about our mission, values, and digital marketing expertise." />
        <link rel="canonical" href="https://losangelesseogroup.com/about" />
        <meta name="keywords" content="Los Angeles SEO company, DesertSEO team, SEO experts, SEO specialists, digital marketing professionals" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Los Angeles SEO Services",
            image: "https://losangelesseogroup.com/og-image.png",
            "@id": "https://losangelesseogroup.com/",
            url: "https://losangelesseogroup.com/",
            telephone: "+1-323-784-1577",
            address: {
              "@type": "PostalAddress",
              streetAddress: "10000 Washington Blvd",
              addressLocality: "Culver City",
              addressRegion: "CA",
              postalCode: "90232",
              addressCountry: "US",
            },
            areaServed: ["Culver City", "West Los Angeles", "Santa Monica", "Mar Vista"],
            priceRange: "$$",
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "127",
            },
          })}
        </script>
      </Helmet>

      <Navbar />
      
      <section className="pt-24 pb-16 bg-gradient-to-r from-desert-sky/10 to-desert-sunset/10">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-gradient bg-sunset-gradient">Passionate</span> About Search Excellence
            </h1>
            <p className="text-xl text-foreground/70 mb-10 leading-relaxed">
              We're a team of SEO specialists dedicated to helping businesses in Los Angeles and beyond achieve sustainable growth through search engine optimization.
            </p>
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-background">
        <div className="page-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sunset/10 text-desert-sunset">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                From Startup to Los Angeles' <span className="text-gradient bg-canyon-gradient">Leading SEO Partner</span>
              </h2>
              <p className="text-lg mb-6 text-foreground/80">
                Founded in 2015, DesertSEO began with a simple mission: to provide transparent, results-driven SEO services that actually deliver on their promises. In an industry often plagued by false guarantees and black-hat techniques, we set out to be different.
              </p>
              <p className="text-lg mb-6 text-foreground/80">
                Our founder, Michael Chen, had seen firsthand how businesses were being misled by SEO companies that prioritized quick wins over sustainable growth. He assembled a team of experts who shared his passion for ethical, data-driven SEO practices.
              </p>
              <p className="text-lg text-foreground/80">
                Today, we've helped over 250 businesses across Los Angeles and beyond achieve meaningful growth through search. Our team has expanded, but our core values remain unchanged: transparency, integrity, and measurable results.
              </p>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80" 
                  alt="DesertSEO office in Los Angeles" 
                  className="w-full h-auto rounded-lg"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-desert-gradient p-4 rounded-lg shadow-lg">
                <p className="text-white font-bold">Est. 2015</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-muted/50">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-cactus/10 text-desert-cactus">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Principles That <span className="text-gradient bg-cactus-gradient">Drive Us</span>
            </h2>
            <p className="text-lg text-foreground/70">
              Our approach to SEO is built on a foundation of core values that guide every strategy, recommendation, and decision we make.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Award className="w-10 h-10 text-desert-sunset" />,
                title: "Excellence",
                description: "We're committed to delivering the highest quality work in everything we do, from technical audits to content strategy.",
              },
              {
                icon: <Globe className="w-10 h-10 text-desert-sky" />,
                title: "Transparency",
                description: "We believe in complete transparency about our methods, reporting, and realistic expectations for results.",
              },
              {
                icon: <Users className="w-10 h-10 text-desert-cactus" />,
                title: "Partnership",
                description: "We view ourselves as an extension of your team, working collaboratively to achieve your business goals.",
              },
              {
                icon: <Clock className="w-10 h-10 text-desert-canyon" />,
                title: "Innovation",
                description: "We stay ahead of algorithm changes and industry trends to keep your SEO strategy cutting-edge.",
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glassmorphism dark:glassmorphism-dark rounded-xl p-6 text-center"
              >
                <div className="mx-auto w-16 h-16 flex items-center justify-center bg-background rounded-full mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-foreground/70">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-background">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sky/10 text-desert-sky">
              Our Team
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Meet the <span className="text-gradient bg-sky-gradient">Experts</span> Behind Our Success
            </h2>
            <p className="text-lg text-foreground/70">
              Our diverse team brings together expertise in technical SEO, content strategy, local search, and analytics to deliver comprehensive solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {isLoadingImages ? (
              Array(6).fill(0).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="bg-background border border-border rounded-xl p-6 shadow-sm"
                >
                  <div className="mb-6 flex justify-center">
                    <Skeleton className="w-40 h-40 rounded-full" />
                  </div>
                  <div className="text-center space-y-3">
                    <Skeleton className="h-6 w-3/4 mx-auto" />
                    <Skeleton className="h-4 w-1/2 mx-auto" />
                    <Skeleton className="h-16 w-full mx-auto" />
                  </div>
                </div>
              ))
            ) : (
              teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="mb-6 flex justify-center">
                    <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-100">
                      <img
                        src={teamImages[member.image] || ""}
                        alt={`${member.name}, ${member.position} at DesertSEO`}
                        width={160}
                        height={160}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={handleImageError}
                      />
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold mb-1 overflow-hidden text-ellipsis" title={member.name}>
                      {member.name}
                    </h3>
                    <p className="text-sm text-desert-sunset mb-4">{member.position}</p>
                    <p className="text-foreground/70 text-sm">{member.bio}</p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-gradient-to-r from-desert-sunset/10 to-desert-canyon/10">
        <div className="page-container">
          <div className="max-w-3xl mx-auto mb-16">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-canyon/10 text-desert-canyon">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                What Sets <span className="text-gradient bg-sunset-gradient">DesertSEO</span> Apart
              </h2>
            </div>
            
            <div className="mt-12 space-y-8">
              {[
                {
                  title: "Local Los Angeles Expertise",
                  description: "We understand the unique challenges and opportunities of the Los Angeles market, with specialized local SEO strategies to help you compete effectively.",
                },
                {
                  title: "Transparent Reporting",
                  description: "Our comprehensive reporting shows exactly what we're doing, what's working, and the real impact on your business metrics.",
                },
                {
                  title: "Custom Strategies",
                  description: "No cookie-cutter approaches here. We develop strategies tailored to your specific industry, goals, and competitive landscape.",
                },
                {
                  title: "Continuous Optimization",
                  description: "SEO is never 'done' - we continuously monitor, test, and refine our approach to maximize your results over time.",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="mt-1">
                    <Check className="w-6 h-6 text-desert-cactus" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-foreground/70">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-16 bg-gradient-to-r from-desert-sunset to-desert-canyon">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Grow Your Business with SEO?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Let's discuss how our team can help you achieve your goals and dominate search results.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Button
                asChild
                size="lg"
                className="bg-white text-desert-sunset hover:bg-white/90"
              >
                <Link to="/contact">
                  Contact Our Team
                  <ArrowUpRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Link to="/case-studies">
                  View Our Work
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </PageTransition>
  );
};

export default About;
